import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderTree, Plus 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FormModal, FormField, FormInput, FormTextarea 
} from '@/components/ui/form-modal';
import { toast } from 'sonner';
import { CATEGORIES } from './catalog-constants';

interface CategoriesViewProps {
  readonly categoryCounts: Record<string, number>;
  readonly setSelectedCategory: (category: string) => void;
}

export function CategoriesView({
  categoryCounts, setSelectedCategory 
}: CategoriesViewProps) {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    navigate('/catalog/items');
  };

  const handleAddCategory = () => {
    setFormData({
      name: '',
      description: '' 
    });
    setShowAddModal(true);
  };

  const handleSaveCategory = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    toast.success(`Created category: ${formData.name}`);
    setShowAddModal(false);
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={handleAddCategory}>
          <Plus className="mr-2 h-4 w-4" />
          Add Category
        </Button>
      </div>

      <FormModal
        title="Add Category"
        description="Create a new category for organizing catalog items"
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveCategory}
        saveLabel="Create"
        size="md"
      >
        <FormField label="Category Name" required>
          <FormInput
            value={formData.name}
            onChange={(e) => setFormData({
              ...formData,
              name: e.target.value 
            })}
            placeholder="e.g., Frozen Seafood"
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            value={formData.description}
            onChange={(e) => setFormData({
              ...formData,
              description: e.target.value 
            })}
            placeholder="Optional description for this category..."
            rows={3}
          />
        </FormField>
      </FormModal>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {CATEGORIES.filter((c) => c !== 'All').map((category) => (
          <Card
            key={category}
            className="cursor-pointer transition-shadow hover:shadow-md"
            onClick={() => handleCategoryClick(category)}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-medium">{category}</CardTitle>
              <FolderTree className="h-5 w-5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{categoryCounts[category] ?? 0}</div>
              <p className="text-sm text-muted-foreground">items in category</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
